import type { DeviceOption } from "./epocus-exam-constants";

/**
 * Rules that map DICOM device identity (manufacturer / model, as reported by
 * the PACS) to a DEVICE_CATALOGUE id. Kept as a single table so adding a
 * device stays one edit here alongside DEVICE_CATALOGUE.
 *
 * `modelIncludes` matches only the model name; `anyIncludes` matches either the
 * manufacturer or the model. All matching is case-insensitive substring.
 * Rules are evaluated top to bottom; the first match wins.
 */
const DEVICE_DICOM_RULES: ReadonlyArray<{
  deviceId: DeviceOption["id"];
  modelIncludes?: readonly string[];
  anyIncludes?: readonly string[];
}> = [
  { deviceId: "ge-vscan-air", modelIncludes: ["vscan"] },
  { deviceId: "butterfly-iq-plus", anyIncludes: ["butterfly"] },
  { deviceId: "sonostar", anyIncludes: ["sonostar"] },
];

/**
 * Resolves the DICOM device identity to a DEVICE_CATALOGUE id, or null when the
 * device is unrecognised (or both inputs are empty/null).
 */
export const matchDeviceFromDicom = (
  manufacturer: string | null,
  model: string | null,
): string | null => {
  const mfr = (manufacturer ?? "").toLowerCase();
  const mdl = (model ?? "").toLowerCase();
  if (!mfr && !mdl) return null;

  for (const rule of DEVICE_DICOM_RULES) {
    if (rule.modelIncludes?.some((needle) => mdl.includes(needle))) {
      return rule.deviceId;
    }
    if (
      rule.anyIncludes?.some(
        (needle) => mfr.includes(needle) || mdl.includes(needle),
      )
    ) {
      return rule.deviceId;
    }
  }
  return null;
};

type DeviceIdentity = {
  id?: string;
  deviceManufacturer?: string | null;
  deviceModelName?: string | null;
  meta: { updatedAt: string };
};

/**
 * Resolves a single scan-derived device id from a set of imaging instances.
 *
 * Edge case: if attached instances map to *different* devices (e.g. a study
 * that mixes probes), the most recently updated instance wins — its identity is
 * the freshest signal of what actually produced the scan. `id` breaks ties on
 * equal timestamps so the result is fully deterministic.
 */
export const matchDeviceFromInstances = (
  instances: ReadonlyArray<DeviceIdentity>,
): string | null => {
  const sorted = [...instances].sort((a, b) => {
    const byUpdatedAt = b.meta.updatedAt.localeCompare(a.meta.updatedAt);
    if (byUpdatedAt !== 0) return byUpdatedAt;
    return (b.id ?? "").localeCompare(a.id ?? "");
  });
  for (const instance of sorted) {
    const matched = matchDeviceFromDicom(
      instance.deviceManufacturer ?? null,
      instance.deviceModelName ?? null,
    );
    if (matched) return matched;
  }
  return null;
};
